import './BotErrorWindow.styles.css';

import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { OverlayPanel } from 'primereact/overlaypanel';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { useOverlayClickOutside } from '../../hooks/useOverlayClickOutside';
import { useWebsocketSubscription } from '../../hooks/useWebsocketSubscription';
import botErrorStore from '../../store/BotErrorStore';
import playerStore from '../../store/PlayerStore';
import { WEventsDispatcher } from '../../websocket/handlers/WEventsDispatcher';
import { MessageBox } from '../shared/MessageBox/MessageBox';
import { BotErrorPlayer, BotErrorWindowProps } from './BotErrorWindow.types';

const ALL_BOTS: BotErrorPlayer = { pid: 'All bots', nick: 'All bots' };

const BotErrorWindow: FC<BotErrorWindowProps> = ({ table, toggleEvent }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [botPlayers, setBotPlayers] = useState<BotErrorPlayer[]>([]);
  const [selectedBot, setSelectedBot] = useState<BotErrorPlayer>(ALL_BOTS);
  const [expanded, setExpanded] = useState(false);

  const overlayRef = useRef<OverlayPanel>(null);
  const messagesElementRef = useRef<HTMLDivElement>(null);

  useWebsocketSubscription(
    `/user/tables/${table.id}/bot-errors`,
    WEventsDispatcher.processEventMessage,
    table.id,
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (overlayRef.current && botErrorStore.getLastOpenedError()) {
      setSelectedBot(botErrorStore.getLastOpenedError());
    }
  }, [botErrorStore.lastOpenedError, isOpen]);

  const botErrors = useMemo(
    () => (isOpen ? botErrorStore.getBotErrors(table.id) : []),
    [botErrorStore.botErrorsStateChanged, isOpen],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const uniquePids = new Set();
    const botPlayers = [...playerStore.findBots(table.players), ...botErrors];
    const uniqueBotPlayers = botPlayers.filter(botPlayer => {
      if (uniquePids.has(botPlayer.pid)) {
        return false;
      } else {
        uniquePids.add(botPlayer.pid);
        return true;
      }
    });
    setBotPlayers(uniqueBotPlayers);
  }, [table.players, botErrors, isOpen]);

  useEffect(() => {
    if (overlayRef.current && toggleEvent) {
      overlayRef.current.toggle(toggleEvent);
    } else if (overlayRef.current) {
      overlayRef.current.hide();
    }
  }, [toggleEvent]);

  const scrollChatEnd = (): void => {
    const messagesElement = messagesElementRef.current;
    if (messagesElement) {
      messagesElement.scrollTop = messagesElement.scrollHeight;
    }
  };

  useEffect(() => {
    if (isOpen) {
      scrollChatEnd();
      if (selectedBot === ALL_BOTS) {
        botErrorStore.markAllBotErrorsAsRead(table.id);
      } else {
        botErrorStore.markBotErrorsAsRead(
          table.id,
          botPlayers.find(player => player.pid === selectedBot.pid)?.pid,
        );
      }
    }
  }, [isOpen, botErrors.length, selectedBot]);

  useOverlayClickOutside(overlayRef);

  const getOptions = (): BotErrorPlayer[] => [
    ALL_BOTS,
    ...botPlayers.filter(player => player),
  ];

  const onHide = (): void => {
    botErrorStore.removeLastOpenedError();
    botErrorStore.toggleBotErrorEventMap.delete(table.id);
    setIsOpen(false);
    setExpanded(false);
    setSelectedBot(ALL_BOTS);
  };

  return (
    <OverlayPanel
      onShow={() => setIsOpen(true)}
      onHide={() => onHide()}
      className={`mt-2 ${expanded ? 'maximized-overlay' : 'bot-error-overlay'}`}
      ref={overlayRef}
      showCloseIcon
      dismissable={false}
      data-ignore-outside-click
    >
      <div className="header">
        <h3 className="m-0">AI Errors</h3>
        <Button
          text
          icon={
            expanded
              ? 'pi pi-arrow-down-left-and-arrow-up-right-to-center'
              : 'pi pi-arrow-up-right-and-arrow-down-left-from-center'
          }
          onClick={() => setExpanded(!expanded)}
        />
      </div>
      <Dropdown
        options={getOptions()}
        value={selectedBot}
        optionLabel="nick"
        placeholder="Bot"
        onChange={e => setSelectedBot(e.value)}
      />
      <div ref={messagesElementRef} className="bot-error-messages-wrapper p-3">
        {botErrors.map(botError =>
          selectedBot === ALL_BOTS || selectedBot.pid === botError.pid ? (
            <MessageBox
              key={botError.id}
              style={{ background: 'rgba(104,13,13,0.94)' }}
              message={botError}
            />
          ) : (
            <React.Fragment key={botError.id} />
          ),
        )}
      </div>
    </OverlayPanel>
  );
};

export default observer(BotErrorWindow);
